import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ReportReason } from '@rvc/protocol';
import type { ChatController, ChatState } from './core/controller';
import { captureFrame } from './core/frame';
import { detectLang, dictionaries, type Key } from './i18n';
import { useChat } from './useChat';

const WS_URL = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
const RULES_URL = import.meta.env.VITE_RULES_URL as string | undefined;
const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL as string | undefined;

const REPORT_REASONS = ['nudity', 'harassment', 'minor', 'spam', 'other'] as const;

export function App() {
  const lang = detectLang();
  const dict = dictionaries[lang];
  const t = (k: Key) => dict[k];
  document.documentElement.lang = lang;
  document.title = dict.title;

  const { chat, state } = useChat(WS_URL, lang);
  const gate = state.status === 'idle' || state.status === 'error';

  return gate ? <Gate chat={chat} state={state} t={t} /> : <ChatScreen chat={chat} state={state} t={t} />;
}

type T = (k: Key) => string;
interface ScreenProps {
  chat: ChatController;
  state: ChatState;
  t: T;
}

// ---------------------------------------------------------------- вхід

function Gate({ chat, state, t }: ScreenProps) {
  const [agreed, setAgreed] = useState(false);
  const errorKey = state.error ? (`err_${state.error}` as Key) : null;
  const fatal =
    state.error === 'banned' ||
    state.error === 'restricted' ||
    state.error === 'replaced' ||
    state.error === 'version';

  return (
    <main className="gate" data-status={state.status}>
      <h1>{t('title')}</h1>
      <p className="sub">{t('subtitle')}</p>

      {errorKey && (
        <p className="error" role="alert" data-testid="error">
          {t(errorKey)}
        </p>
      )}

      {!fatal && (
        <>
          <label className="check">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="age-check"
            />
            <span>{t('ageConfirm')}</span>
          </label>
          <button className="btn primary big" disabled={!agreed} onClick={() => void chat.start()} data-testid="start">
            {state.error ? t('retry') : t('start')}
          </button>
        </>
      )}

      <Links t={t} />
    </main>
  );
}

function Links({ t }: { t: T }) {
  if (!RULES_URL && !PRIVACY_URL) return null;
  return (
    <p className="links">
      {RULES_URL && (
        <a href={RULES_URL} target="_blank" rel="noopener">
          {t('rules')}
        </a>
      )}
      {PRIVACY_URL && (
        <a href={PRIVACY_URL} target="_blank" rel="noopener">
          {t('privacy')}
        </a>
      )}
    </p>
  );
}

// ---------------------------------------------------------------- чат

function ChatScreen({ chat, state, t }: ScreenProps) {
  const remoteRef = useRef<HTMLVideoElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    if (localRef.current) localRef.current.srcObject = state.localStream;
  }, [state.localStream]);
  useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = state.remoteStream;
  }, [state.remoteStream]);

  const searching = state.status === 'searching' || state.status === 'pairing' || state.status === 'connected';
  const connected = state.status === 'connected';

  let message: string | null = null;
  if (state.rateLimited) message = t('rateLimited');
  else if (state.blocked && !connected) message = t('blocked');
  else if (state.reported && !connected) message = t('reported');
  else if (state.lastPeerLeft && state.status === 'searching') {
    message = state.lastPeerLeft === 'timeout' ? t('peer_timeout') : t('peer_left');
  } else if (!connected) {
    message = t(`status_${state.status}` as Key);
  }

  return (
    <main className="chat" data-status={state.status} data-testid="chat">
      <div className="stage">
        <video ref={remoteRef} className="remote" autoPlay playsInline data-testid="remote" />
        {message && (
          <div className="overlay" role="status" data-testid="overlay">
            <span className={connected ? '' : 'pulse'}>{message}</span>
            {state.status === 'searching' && state.waiting > 1 && <small>{state.waiting}</small>}
          </div>
        )}
        <video
          ref={localRef}
          className={`pip ${state.camOn ? '' : 'off'}`}
          autoPlay
          playsInline
          muted
          data-testid="local"
          aria-label={t('you')}
        />
        {state.online !== null && (
          <span className="badge">
            {state.online} {t('online')}
          </span>
        )}
      </div>

      <div className="controls">
        <IconButton
          label={state.micOn ? t('mute') : t('unmute')}
          pressed={!state.micOn}
          onClick={() => chat.toggleMic()}
          testId="mic"
        >
          {state.micOn ? <MicIcon /> : <MicOffIcon />}
        </IconButton>
        <IconButton
          label={state.camOn ? t('camOff') : t('camOn')}
          pressed={!state.camOn}
          onClick={() => chat.toggleCam()}
          testId="cam"
        >
          {state.camOn ? <CamIcon /> : <CamOffIcon />}
        </IconButton>

        {searching ? (
          <button className="btn primary grow" onClick={() => chat.next()} data-testid="next">
            {t('next')}
          </button>
        ) : (
          <button className="btn primary grow" onClick={() => void chat.start()} data-testid="start-again">
            {t('start')}
          </button>
        )}

        <button className="btn" onClick={() => chat.stop()} disabled={!searching} data-testid="stop">
          {t('stop')}
        </button>
        <IconButton label={t('report')} onClick={() => setReporting(true)} disabled={!connected} testId="report">
          <FlagIcon />
        </IconButton>
        <IconButton label={t('block')} onClick={() => chat.block()} disabled={!connected} testId="block">
          <BlockIcon />
        </IconButton>
      </div>

      {reporting && (
        <ReportDialog
          t={t}
          onCancel={() => setReporting(false)}
          onSend={(reason) => {
            chat.report(reason, captureFrame(remoteRef.current));
            setReporting(false);
          }}
        />
      )}
    </main>
  );
}

function ReportDialog({
  t,
  onSend,
  onCancel,
}: {
  t: T;
  onSend: (reason: ReportReason) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<ReportReason>('nudity');
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={t('reportTitle')}>
      <div className="modal">
        <h2>{t('reportTitle')}</h2>
        {REPORT_REASONS.map((r) => (
          <label key={r} className="radio">
            <input
              type="radio"
              name="reason"
              checked={reason === r}
              onChange={() => setReason(r)}
              data-testid={`reason-${r}`}
            />
            <span>{t(`reason_${r}` as Key)}</span>
          </label>
        ))}
        <div className="row">
          <button className="btn" onClick={onCancel}>
            {t('cancel')}
          </button>
          <button className="btn primary" onClick={() => onSend(reason)} data-testid="report-send">
            {t('reportSend')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- дрібні компоненти

function IconButton(props: {
  label: string;
  children: ReactNode;
  onClick: () => void;
  pressed?: boolean;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      className="btn icon"
      aria-label={props.label}
      title={props.label}
      aria-pressed={props.pressed}
      disabled={props.disabled}
      onClick={props.onClick}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

const svg = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const MicIcon = () => (
  <svg {...svg}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </svg>
);
const MicOffIcon = () => (
  <svg {...svg}>
    <path d="M3 3l18 18M9 9v2a3 3 0 0 0 5 2M15 9V6a3 3 0 0 0-6-.5M5 11a7 7 0 0 0 11 5.5M19 11a7 7 0 0 1-.6 2.8M12 18v3" />
  </svg>
);
const CamIcon = () => (
  <svg {...svg}>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10l5-3v10l-5-3z" />
  </svg>
);
const CamOffIcon = () => (
  <svg {...svg}>
    <path d="M3 3l18 18M16 11V8a2 2 0 0 0-2-2H8M3 8v8a2 2 0 0 0 2 2h9M16 10l5-3v10l-4-2.4" />
  </svg>
);
const FlagIcon = () => (
  <svg {...svg}>
    <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
  </svg>
);
const BlockIcon = () => (
  <svg {...svg}>
    <circle cx="12" cy="12" r="9" />
    <path d="M5.6 5.6l12.8 12.8" />
  </svg>
);
